/**
 * ProjectionChart Component
 *
 * Displays 10-year financial projections with NOI, debt service, and cash flow.
 * After-tax cumulative line includes bonus depreciation (OBBB) tax benefits.
 *
 * Color coding:
 * - Emerald: NOI (income)
 * - Rose: Debt Service (expense)
 * - Blue: Cash Flow (result)
 * - Amber: After-Tax Cumulative Return (includes OBBB bonus depreciation)
 */

import { Card, AreaChart } from '@tremor/react';
import { formatCurrency } from '../../utils/formatters';
import type { YearProjection } from '../../types/calculations';

export interface ProjectionChartProps {
  /** Array of yearly projections (years 0-10) */
  projections: YearProjection[];
  /** Whether to show cumulative cash flow line */
  showCumulative?: boolean;
}

/**
 * Value formatter for chart tooltips and axis labels
 */
function valueFormatter(value: number): string {
  return formatCurrency(value);
}

export function ProjectionChart({ projections, showCumulative = true }: ProjectionChartProps) {
  // Filter out year 0 (acquisition year) and format data for chart
  const operatingYears = projections.filter((p) => p.year > 0);

  const chartData = operatingYears.map((p) => ({
    yearLabel: `Year ${p.year}`,
    'NOI': p.noi,
    'Debt Service': p.debt_service,
    'Cash Flow': p.cash_flow,
    ...(showCumulative && {
      'After-Tax Cumulative': p.after_tax_cumulative_cf ?? p.cumulative_cash_flow,
    }),
  }));

  // Calculate the OBBB bonus impact for the annotation
  const year10 = operatingYears.find(p => p.year === 10);
  const afterTaxCum10 = year10?.after_tax_cumulative_cf;
  const preTaxCum10 = year10?.cumulative_cash_flow;
  const taxImpact = afterTaxCum10 != null && preTaxCum10 != null
    ? afterTaxCum10 - preTaxCum10
    : null;

  // Build categories array based on showCumulative
  const categories = showCumulative
    ? ['NOI', 'Debt Service', 'Cash Flow', 'After-Tax Cumulative']
    : ['NOI', 'Debt Service', 'Cash Flow'];

  const colors = showCumulative
    ? ['emerald', 'rose', 'blue', 'amber']
    : ['emerald', 'rose', 'blue'];

  return (
    <Card className="p-4">
      {/* Title - using Tailwind since Tremor v4 removed Title component */}
      <h3 className="font-display text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
        10-Year Cash Flow Projection
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Year-by-year NOI, debt service, and net cash flow
      </p>

      <AreaChart
        className="h-72"
        data={chartData}
        index="yearLabel"
        categories={categories}
        colors={colors}
        valueFormatter={valueFormatter}
        showLegend={true}
        showGridLines={true}
        curveType="monotone"
        connectNulls={true}
      />

      {/* Legend explanation */}
      <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-emerald-500"></span>
          <span>NOI</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-rose-500"></span>
          <span>Debt Service</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-blue-500"></span>
          <span>Cash Flow</span>
        </div>
        {showCumulative && (
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-amber-500"></span>
            <span>After-Tax Cumulative <span className="text-amber-600 font-medium">(incl. OBBB Bonus Depreciation)</span></span>
          </div>
        )}
      </div>

      {/* OBBB Impact Note */}
      {showCumulative && taxImpact != null && taxImpact > 0 && (
        <div className="mt-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-lg">
          <p className="text-xs text-amber-800 dark:text-amber-300">
            <span className="font-semibold">OBBB Tax Benefit Impact:</span>{' '}
            Cumulative after-tax return includes {formatCurrency(taxImpact)} in depreciation tax savings over 10 years
            (bonus depreciation + building straight-line at 40% effective rate).
          </p>
        </div>
      )}
    </Card>
  );
}

export default ProjectionChart;
